/**
 * Auth routes - login + signup
 *
 * Day-1 SCOPE: **Google OAuth only**. Email OTP deferred (no Resend
 * account provisioned, and OAuth alone covers Beta dogfood UX). Per
 * founder decision 2026-05-05.
 *
 * Flow:
 *   POST /api/v1/auth/google/init -> returns {oauth_url}
 *   FE redirects to oauth_url
 *   Google redirects back to /api/v1/auth/google/callback (Worker)
 *   Worker sets cookies + redirects to ?redirect=...
 *
 * i18n: disclaimer renders single-locale only per BUG-2026-05-05-02
 * (legal-upl HARD VETO - disclaimer must render in user's chosen
 * language; mixed VN+EN weakens "user understood disclaimer"
 * presumption per LEGAL-DIY Sec.3).
 */

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLang } from '../use-lang';

function DisclaimerVN() {
  return (
    <>
      Bằng việc tiếp tục, bạn đồng ý với <Link to="/terms">Điều khoản</Link> +{' '}
      <Link to="/privacy">Chính sách bảo mật</Link> của Asklaw.
    </>
  );
}
function DisclaimerEN() {
  return (
    <>
      By continuing, you agree to Asklaw&apos;s <Link to="/terms">Terms</Link> +{' '}
      <Link to="/privacy">Privacy Policy</Link>.
    </>
  );
}

export function LoginRoute() {
  return <AuthForm mode="login" />;
}

export function SignupRoute() {
  return <AuthForm mode="signup" />;
}

function AuthForm({ mode }: { mode: 'login' | 'signup' }) {
  const [params] = useSearchParams();
  const redirect = params.get('redirect') ?? '/chat';
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { lang } = useLang();

  const isLogin = mode === 'login';

  const t = {
    title: isLogin
      ? lang === 'vi' ? 'Đăng nhập' : 'Sign in'
      : lang === 'vi' ? 'Đăng ký' : 'Sign up',
    switcherPrompt: isLogin
      ? lang === 'vi' ? 'Chưa có tài khoản?' : "Don't have an account?"
      : lang === 'vi' ? 'Đã có tài khoản?' : 'Already have an account?',
    switcherCta: isLogin
      ? lang === 'vi' ? 'Đăng ký' : 'Sign up'
      : lang === 'vi' ? 'Đăng nhập' : 'Sign in',
    cta: isLogin
      ? lang === 'vi' ? 'Đăng nhập với Google' : 'Sign in with Google'
      : lang === 'vi' ? 'Đăng ký với Google' : 'Sign up with Google',
    submitting: lang === 'vi' ? 'Đang chuyển hướng...' : 'Redirecting...',
    backendBusy: lang === 'vi'
      ? 'Backend đang triển khai. Vui lòng quay lại sau ít phút.'
      : 'Backend is provisioning. Please try again in a few minutes.',
    httpError: lang === 'vi' ? 'Lỗi' : 'Error',
    phase2Note: lang === 'vi'
      ? 'Đăng nhập bằng email/OTP sẽ có ở Phase 2.'
      : 'Sign in via email/OTP is coming in Phase 2.',
  };

  async function startGoogleOAuth() {
    setError(null);
    setSubmitting(true);
    try {
      // Backend `redirect_uri` field = the Google OAuth callback URL.
      // Stash post-login destination separately in a cookie so the
      // callback handler can read it after Google redirects back.
      const callbackUrl = `${window.location.origin}/api/v1/auth/google/callback`;
      document.cookie = `asklaw_post_login=${encodeURIComponent(
        redirect
      )}; Path=/; Max-Age=600; SameSite=Lax`;

      const r = await fetch(`/api/v1/auth/google/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ redirect_uri: callbackUrl }),
      });
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as {
          error?: string | { code?: string; message?: string };
        };
        const errCode = typeof j.error === 'string' ? j.error : j.error?.code;
        const errMsg = typeof j.error === 'string' ? j.error : j.error?.message;
        if (errCode === 'ASKLAW_BACKEND_PROVISIONING') {
          throw new Error(t.backendBusy);
        }
        throw new Error(errMsg ?? `${t.httpError} ${r.status}`);
      }
      const j = (await r.json()) as { oauth_url: string };
      window.location.href = j.oauth_url;
    } catch (err) {
      setError((err as Error).message);
      setSubmitting(false);
    }
  }

  return (
    <main style={{ maxWidth: 460, margin: '64px auto', padding: '0 16px' }}>
      <h1 style={{ marginBottom: 8 }}>{t.title}</h1>
      <p style={{ color: 'var(--color-muted)', fontSize: 14 }}>
        {t.switcherPrompt}{' '}
        <Link to={isLogin ? '/auth/signup' : '/auth/login'}>{t.switcherCta}</Link>
      </p>

      <button
        type="button"
        onClick={startGoogleOAuth}
        disabled={submitting}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          padding: '14px 16px',
          marginTop: 32,
          width: '100%',
          border: '1px solid var(--color-border)',
          borderRadius: 8,
          background: 'var(--color-bg)',
          color: 'inherit',
          fontWeight: 600,
          fontSize: 15,
          cursor: submitting ? 'not-allowed' : 'pointer',
          opacity: submitting ? 0.6 : 1,
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
          <path
            fill="#4285F4"
            d="M21.6 12.227c0-.709-.064-1.39-.182-2.045H12v3.868h5.382a4.6 4.6 0 0 1-1.996 3.018v2.51h3.232c1.891-1.742 2.982-4.305 2.982-7.35z"
          />
          <path
            fill="#34A853"
            d="M12 22c2.7 0 4.964-.895 6.618-2.423l-3.232-2.509c-.895.6-2.04.955-3.386.955-2.605 0-4.81-1.76-5.595-4.123H3.064v2.59A9.996 9.996 0 0 0 12 22z"
          />
          <path
            fill="#FBBC05"
            d="M6.405 13.9a5.997 5.997 0 0 1 0-3.8V7.51H3.064a9.996 9.996 0 0 0 0 8.978l3.341-2.59z"
          />
          <path
            fill="#EA4335"
            d="M12 5.977c1.468 0 2.786.505 3.823 1.495l2.868-2.868C16.96 2.99 14.696 2 12 2A9.996 9.996 0 0 0 3.064 7.51l3.341 2.59C7.19 7.737 9.395 5.977 12 5.977z"
          />
        </svg>
        {submitting ? t.submitting : t.cta}
      </button>

      {error ? (
        <div
          role="alert"
          className="banner banner-danger"
          style={{ marginTop: 16, fontSize: 14 }}
        >
          {error}
        </div>
      ) : null}

      <p
        style={{
          marginTop: 32,
          fontSize: 12,
          color: 'var(--color-muted)',
          lineHeight: 1.5,
        }}
        lang={lang}
      >
        {lang === 'vi' ? <DisclaimerVN /> : <DisclaimerEN />}
      </p>

      <p style={{ marginTop: 32, fontSize: 13, color: 'var(--color-muted)' }}>
        {t.phase2Note}
      </p>
    </main>
  );
}
